import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import QuoteForm from '../../../components/feature/QuoteForm';

export default function CostsEstimates() {
  const costFactors = [
    {
      title: 'Project Size',
      description: 'Larger projects benefit from economies of scale with lower per-square-foot costs.',
      icon: 'ri-ruler-line',
    },
    {
      title: 'Site Conditions',
      description: 'Soil quality, drainage, and accessibility affect preparation costs.',
      icon: 'ri-landscape-line',
    },
    {
      title: 'Material Thickness',
      description: 'Thicker asphalt or concrete provides longer life but increases upfront cost.',
      icon: 'ri-stack-line',
    },
    {
      title: 'Base Preparation',
      description: 'Proper base work is critical but adds to project cost.',
      icon: 'ri-building-4-line',
    },
    {
      title: 'Decorative Options',
      description: 'Stamped concrete, colored finishes, and borders increase costs.',
      icon: 'ri-palette-line',
    },
    {
      title: 'Timing',
      description: 'Off-season scheduling may offer cost savings on larger projects.',
      icon: 'ri-calendar-line',
    },
  ];

  const faqs = [
    {
      question: 'How much does asphalt paving cost per square foot in Lincoln NE?',
      answer: 'Asphalt paving costs $3-$7 per square foot installed in Lincoln. A standard 20x20 (400 sq ft) driveway costs $1,200-$2,800. Prices include excavation, base preparation, 3-4 inches of asphalt, and compaction. Larger projects, simpler sites, and thinner asphalt reduce per-square-foot costs.',
    },
    {
      question: 'What is the cost difference between asphalt and concrete driveways?',
      answer: 'Asphalt costs $3-$7 per square foot, concrete costs $6-$12 per square foot. A 400 sq ft driveway costs $1,200-$2,800 for asphalt vs $2,400-$4,800 for concrete. However, concrete lasts 30-40 years vs 15-20 for asphalt and requires less maintenance, making lifetime costs comparable.',
    },
    {
      question: 'How much does sealcoating cost?',
      answer: 'Sealcoating costs $0.15-$0.25 per square foot for residential driveways. A 1,000 sq ft driveway costs $150-$250 including cleaning, crack filling, and two coats of sealer. Commercial parking lots cost $0.12-$0.20 per square foot. Sealcoating every 2-3 years extends pavement life by 10-15 years.',
    },
    {
      question: 'What affects paving costs the most?',
      answer: 'Site preparation is the biggest variable. Poor soil requires more base work. Steep slopes need extra grading. Limited access requires smaller equipment and more labor. Removal of existing pavement adds $1-$3 per square foot. We provide detailed estimates after site inspection to identify all cost factors.',
    },
    {
      question: 'Do you offer financing for paving projects?',
      answer: 'Yes! We work with financing partners to offer payment plans for qualified customers. Financing options include 12-60 month terms with competitive rates. This makes quality paving affordable with manageable monthly payments. Contact us to discuss financing options for your project.',
    },
    {
      question: 'How can I reduce paving costs?',
      answer: 'Several strategies reduce costs: choose asphalt over concrete, minimize decorative options, schedule during off-season, handle some prep work yourself (clearing vegetation), combine projects with neighbors for volume discounts, or phase work over multiple years. We help you find cost-effective solutions that meet your needs and budget.',
    },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-primary-500 text-sm mb-4 tracking-wider">Services / Pricing</p>
            <h1 className="text-5xl md:text-6xl font-black mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Paving Costs &amp; Estimates
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Transparent pricing and free quotes for asphalt and concrete paving in Lincoln, Nebraska
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Pricing Overview */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Average Paving Costs in Lincoln NE
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-primary-600 to-primary-700 p-8 rounded-2xl text-white">
                <h3 className="text-2xl font-bold mb-4">Asphalt Driveway</h3>
                <p className="text-5xl font-black mb-2">$3-$7</p>
                <p className="text-xl mb-4">per square foot</p>
                <p className="text-orange-100">Includes excavation, base, and 3-4 inches of asphalt</p>
              </div>
              <div className="bg-gradient-to-br from-gray-700 to-gray-800 p-8 rounded-2xl text-white">
                <h3 className="text-2xl font-bold mb-4">Concrete Driveway</h3>
                <p className="text-5xl font-black mb-2">$6-$12</p>
                <p className="text-xl mb-4">per square foot</p>
                <p className="text-gray-300">Includes excavation, base, and 4 inches of concrete</p>
              </div>
              <div className="bg-gradient-to-br from-gray-600 to-gray-700 p-8 rounded-2xl text-white">
                <h3 className="text-2xl font-bold mb-4">Sealcoating</h3>
                <p className="text-5xl font-black mb-2">$0.15-$0.25</p>
                <p className="text-xl mb-4">per square foot</p>
                <p className="text-gray-200">Includes cleaning, crack filling, and two coats</p>
              </div>
            </div>
          </div>

          {/* Detailed Pricing */}
          <div className="prose prose-lg max-w-none mb-16">
            <h2 className="text-4xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Detailed Paving Cost Breakdown
            </h2>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Understanding paving costs helps you budget effectively and make informed decisions. Prices vary based on project size, site conditions, materials, and complexity. We provide transparent pricing with detailed estimates that explain all costs.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Asphalt Driveway Costs
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Asphalt driveways cost $3-$7 per square foot installed in Lincoln NE. A typical residential driveway (20x20 feet, 400 sq ft) costs $1,200-$2,800. Larger driveways benefit from economies of scale with lower per-square-foot costs.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Cost breakdown: Excavation and site prep ($0.50-$1.00/sq ft), gravel base 4-6 inches ($0.75-$1.50/sq ft), asphalt 3-4 inches ($2.00-$4.00/sq ft), compaction and finishing ($0.25-$0.50/sq ft). Additional costs include removing existing pavement ($1-$3/sq ft), poor soil remediation ($2-$5/sq ft), and decorative borders ($5-$15/linear ft).
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Example costs: 400 sq ft driveway: $1,200-$2,800, 600 sq ft driveway: $1,800-$4,200, 1,000 sq ft driveway: $3,000-$7,000. These prices include complete installation with proper base preparation and compaction.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Concrete Driveway Costs
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Concrete driveways cost $6-$12 per square foot for standard finishes. A 400 sq ft driveway costs $2,400-$4,800. Stamped or colored concrete costs $10-$18 per square foot. While concrete costs more upfront, it lasts 30-40 years vs 15-20 for asphalt.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Cost breakdown: Excavation and site prep ($0.50-$1.00/sq ft), gravel base 4-6 inches ($0.75-$1.50/sq ft), concrete 4 inches with fiber reinforcement ($4.00-$8.00/sq ft), finishing and curing ($0.50-$1.00/sq ft). Decorative options add: stamped patterns ($4-$6/sq ft), integral color ($1-$2/sq ft), exposed aggregate ($2-$4/sq ft).
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Example costs: 400 sq ft standard concrete: $2,400-$4,800, 400 sq ft stamped concrete: $4,000-$7,200, 600 sq ft standard concrete: $3,600-$7,200. Concrete requires minimal maintenance but costs more initially than asphalt.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Maintenance and Repair Costs
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Regular maintenance extends pavement life and prevents expensive repairs. Sealcoating costs $0.15-$0.25 per square foot every 2-3 years. A 1,000 sq ft driveway costs $150-$250 to sealcoat. This small investment prevents $3,000-$7,000 replacement costs.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Repair costs: Crack filling ($1-$3 per linear foot), pothole patching ($100-$300 per hole), edge repair ($5-$10 per linear foot), resurfacing ($2-$4 per square foot). Emergency repairs may include additional service fees.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Lifetime maintenance costs: Asphalt driveway over 20 years requires 6-8 sealcoating applications ($900-$2,000 total) plus crack filling and minor repairs ($500-$1,000). Concrete requires minimal maintenance (sealing every 2-3 years, $300-$600 total over 30 years).
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Commercial Paving Costs
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Commercial parking lots cost $3-$7 per square foot for asphalt, similar to residential but with volume discounts on larger projects. A 10,000 sq ft parking lot costs $30,000-$70,000 including base work, asphalt, striping, and ADA compliance.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Additional commercial costs: Parking lot striping ($0.15-$0.30 per linear foot), handicap symbols ($50-$100 each), catch basins ($500-$1,500 each), concrete curbs ($8-$15 per linear foot), ADA ramps ($1,000-$3,000 each).
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Commercial maintenance: Sealcoating ($0.12-$0.20 per square foot), crack filling ($1-$3 per linear foot), pothole repair ($100-$300 per hole), restriping ($800-$1,500 for 50-space lot). Maintenance contracts offer discounted rates and priority scheduling.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Factors Affecting Paving Costs
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Several factors influence final paving costs. Site conditions are the biggest variable - poor soil requires more base work, steep slopes need extra grading, and limited access requires smaller equipment and more labor. We assess your site and provide accurate estimates.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Project size affects per-square-foot costs. Larger projects benefit from economies of scale. A 200 sq ft driveway might cost $7-$10 per square foot, while a 1,000 sq ft driveway costs $3-$5 per square foot. Minimum project fees apply for very small jobs.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Material choices impact costs. Thicker asphalt (4-5 inches vs 3 inches) adds $1-$2 per square foot but extends life. Decorative concrete costs more than standard finishes. Premium sealers cost more but last longer. We help you balance cost and quality.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Getting Accurate Estimates
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Accurate estimates require site inspection. We measure the area, assess soil conditions, evaluate drainage, check access, and discuss your preferences. This allows us to provide detailed, accurate quotes with no surprises.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Our estimates include: complete scope of work, material specifications, labor costs, equipment costs, timeline, payment terms, and warranty information. We explain all costs and answer your questions. Free estimates are provided within 24-48 hours of site visit.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Beware of estimates without site visits - they're often inaccurate. Low-ball estimates may exclude necessary work or use inferior materials. We provide honest, transparent pricing that reflects quality workmanship and materials.
            </p>
          </div>

          {/* Cost Factors Grid */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              What Affects Paving Costs?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {costFactors.map((factor, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-2xl border-2 border-gray-200 hover:border-primary-600 transition-all">
                  <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mb-6">
                    <i className={`${factor.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {factor.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {factor.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Comparison Table */}
          <div className="mb-16 overflow-x-auto">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Asphalt vs Concrete Cost Comparison
            </h2>
            <table className="w-full bg-white border-2 border-gray-200 rounded-2xl overflow-hidden">
              <thead className="bg-gray-900 text-white">
                <tr>
                  <th className="p-4 text-left">Factor</th>
                  <th className="p-4 text-left">Asphalt</th>
                  <th className="p-4 text-left">Concrete</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="p-4 font-semibold">Initial Cost</td>
                  <td className="p-4">$3-$7 per sq ft</td>
                  <td className="p-4">$6-$12 per sq ft</td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="p-4 font-semibold">Lifespan</td>
                  <td className="p-4">15-20 years</td>
                  <td className="p-4">30-40 years</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="p-4 font-semibold">Maintenance</td>
                  <td className="p-4">Sealcoat every 2-3 years</td>
                  <td className="p-4">Seal every 2-3 years (optional)</td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="p-4 font-semibold">Repair Ease</td>
                  <td className="p-4">Easy, affordable</td>
                  <td className="p-4">Difficult, expensive</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="p-4 font-semibold">Climate Performance</td>
                  <td className="p-4">Good in freeze-thaw</td>
                  <td className="p-4">Excellent durability</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="p-4 font-semibold">Design Options</td>
                  <td className="p-4">Limited (black surface)</td>
                  <td className="p-4">Extensive (stamped, colored)</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* FAQs */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-2xl border-2 border-gray-200">
                  <h3 className="text-2xl font-bold mb-4 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {faq.question}
                  </h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Quote Form */}
          <div className="bg-gray-50 p-12 rounded-2xl">
            <h2 className="text-4xl font-bold mb-6 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Get Your Free Paving Quote
            </h2>
            <p className="text-xl text-gray-700 text-center mb-8">
              Transparent pricing with detailed estimates for your project
            </p>
            <QuoteForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
